import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Color from '@tiptap/extension-color';
import { TextStyle, FontFamily, FontSize } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon, Unlink, Type, Heading1, Heading2, Heading3, Heading4, Palette, X } from 'lucide-react';
import { useDesignColors, useDesignFonts } from '../pages/DashboardLayout';

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];

function ToolbarButton({ active, onClick, children, title, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '4px 7px', border: 'none', borderRadius: 4, cursor: disabled ? 'default' : 'pointer',
        background: active ? 'var(--primary, #CC0000)' : 'transparent',
        color: active ? '#fff' : disabled ? '#ccc' : 'var(--text-secondary, #6B7280)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
      }}
      onMouseEnter={e => { if (!active && !disabled) e.currentTarget.style.background = '#f3f4f6'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 4px' }} />;
}

function ColorPalette({ editor }) {
  const designColors = useDesignColors();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const activeColor = editor?.getAttributes('textStyle').color || '';

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const applyColor = (color) => {
    editor.chain().focus().setColor(color).run();
    setOpen(false);
  };

  const removeColor = () => {
    editor.chain().focus().unsetColor().run();
    setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Textfarbe"
        data-testid="tiptap-color-toggle"
        style={{
          padding: '4px 7px', border: 'none', borderRadius: 4, cursor: 'pointer',
          background: open ? 'var(--primary, #CC0000)' : 'transparent',
          color: open ? '#fff' : 'var(--text-secondary, #6B7280)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = '#f3f4f6'; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}
      >
        <Palette size={16} style={{ color: open ? '#fff' : (activeColor || 'var(--text-secondary)') }} />
      </button>

      {open && designColors.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 50, marginTop: 4,
          background: '#fff', border: '1px solid var(--border, #e5e7eb)', borderRadius: 8,
          padding: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 180,
        }} data-testid="tiptap-color-palette">
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, padding: '0 2px' }}>Design-Farben</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {designColors.map(color => (
              <button
                key={color}
                type="button"
                onClick={() => applyColor(color)}
                title={color}
                data-testid={`color-swatch-${color.replace('#', '')}`}
                style={{
                  width: 26, height: 26, borderRadius: 6, border: activeColor === color ? '2px solid #111' : '1px solid #d1d5db',
                  background: color, cursor: 'pointer', transition: 'transform 0.1s',
                  outline: activeColor === color ? '2px solid #fff' : 'none',
                  outlineOffset: -3,
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              />
            ))}
          </div>
          {activeColor && (
            <button type="button" onClick={removeColor} data-testid="tiptap-color-remove"
              style={{
                marginTop: 6, width: '100%', padding: '4px 6px', fontSize: 11, border: '1px solid #e5e7eb',
                borderRadius: 4, background: '#f9fafb', cursor: 'pointer', color: 'var(--text-secondary)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}>
              <X size={12} /> Farbe entfernen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Toolbar({ editor }) {
  const designFonts = useDesignFonts();
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL eingeben:', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  const fontFamilyValue = (f) => {
    if (f.id === 'system') return '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    if (['Georgia', 'Times New Roman'].includes(f.id)) return `'${f.id}', serif`;
    if (f.id === 'Courier New') return `'${f.id}', monospace`;
    return `'${f.id}', sans-serif`;
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, padding: '6px 8px', borderBottom: '1px solid var(--border, #e5e7eb)', background: '#fafafa', borderRadius: '8px 8px 0 0' }} data-testid="tiptap-toolbar">
      {/* Font Family */}
      <select
        onChange={e => {
          const val = e.target.value;
          if (val) editor.chain().focus().setFontFamily(val).run();
          else editor.chain().focus().unsetFontFamily().run();
        }}
        style={{ fontSize: 12, padding: '3px 4px', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', maxWidth: 140 }}
        title="Schriftart"
        data-testid="tiptap-font-family"
      >
        <option value="">Standard</option>
        {designFonts.filter(f => f.id !== 'system').map(f => (
          <option key={f.id} value={fontFamilyValue(f)}>{f.label}</option>
        ))}
      </select>

      {/* Font Size */}
      <select
        onChange={e => {
          const val = e.target.value;
          if (val) editor.chain().focus().setFontSize(val).run();
          else editor.chain().focus().unsetFontSize().run();
        }}
        style={{ fontSize: 12, padding: '3px 4px', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', width: 60 }}
        title="Schriftgröße"
        data-testid="tiptap-font-size"
      >
        <option value="">Größe</option>
        {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>

      <Divider />

      {/* Headings */}
      <ToolbarButton active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Überschrift 1"><Heading1 size={16} /></ToolbarButton>
      <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Überschrift 2"><Heading2 size={16} /></ToolbarButton>
      <ToolbarButton active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Überschrift 3"><Heading3 size={16} /></ToolbarButton>
      <ToolbarButton active={editor.isActive('heading', { level: 4 })} onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} title="Überschrift 4"><Heading4 size={16} /></ToolbarButton>

      <Divider />

      {/* Format */}
      <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Fett"><Bold size={16} /></ToolbarButton>
      <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Kursiv"><Italic size={16} /></ToolbarButton>
      <ToolbarButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Unterstrichen"><UnderlineIcon size={16} /></ToolbarButton>

      <Divider />

      {/* Color */}
      <ColorPalette editor={editor} />

      <Divider />

      {/* Lists */}
      <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Aufzählung"><List size={16} /></ToolbarButton>
      <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Nummerierung"><ListOrdered size={16} /></ToolbarButton>

      <Divider />

      {/* Links */}
      <ToolbarButton active={editor.isActive('link')} onClick={setLink} title="Link einfügen"><LinkIcon size={16} /></ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive('link')} title="Link entfernen"><Unlink size={16} /></ToolbarButton>
    </div>
  );
}

export default function TipTapEditor({ content, onChange, placeholder, editorRef }) {
  const lastExternalContent = useRef(content || '');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank' } }),
      Underline,
      Color,
      TextStyle,
      FontFamily,
      FontSize,
      Placeholder.configure({ placeholder: placeholder || 'Inhalt eingeben...' }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastExternalContent.current = html;
      onChange?.(html);
    },
  });

  // Expose editor instance via ref
  useEffect(() => {
    if (editorRef) editorRef.current = editor;
  }, [editor, editorRef]);

  // Sync external content changes into editor
  useEffect(() => {
    if (editor && content !== lastExternalContent.current) {
      lastExternalContent.current = content || '';
      editor.commands.setContent(content || '', false);
    }
  }, [content, editor]);

  return (
    <div style={{ border: '1px solid var(--border, #e5e7eb)', borderRadius: 8, overflow: 'hidden' }} data-testid="tiptap-editor">
      <Toolbar editor={editor} />
      <EditorContent
        editor={editor}
        style={{ minHeight: 200 }}
      />
      <style>{`
        .tiptap { padding: 12px 16px; min-height: 200px; outline: none; font-size: 14px; line-height: 1.7; }
        .tiptap p { margin: 0 0 8px; }
        .tiptap h1 { font-size: 28px; font-weight: 700; margin: 16px 0 8px; }
        .tiptap h2 { font-size: 22px; font-weight: 700; margin: 14px 0 6px; }
        .tiptap h3 { font-size: 18px; font-weight: 600; margin: 12px 0 4px; }
        .tiptap h4 { font-size: 16px; font-weight: 600; margin: 10px 0 4px; }
        .tiptap a { color: var(--primary, #CC0000); text-decoration: underline; cursor: pointer; }
        .tiptap ul, .tiptap ol { padding-left: 24px; margin: 8px 0; }
        .tiptap ul { list-style-type: disc; }
        .tiptap ol { list-style-type: decimal; }
        .tiptap li { margin: 4px 0; }
        .tiptap p.is-editor-empty:first-child::before { color: #adb5bd; content: attr(data-placeholder); float: left; height: 0; pointer-events: none; }
      `}</style>
    </div>
  );
}
